// Pinned tools: the reader's own shortlist, kept in this browser. They lead the sidebar, fill
// the phone dock and open the hub's board. Nothing leaves the device.
import { useCallback, useEffect, useState } from "react";
import { TOOLS, type ToolId } from "./tools";

export const FAVS_KEY = "insnip.more.favs";
export const FAVS_MAX = 6;
/** What the dock shows until the reader pins something of their own. */
export const DEFAULT_DOCK: ToolId[] = ["near", "pay", "housing"];
const EVENT = "insnip:favs";

export function readFavs(): ToolId[] {
  try {
    const raw = JSON.parse(localStorage.getItem(FAVS_KEY) || "[]");
    if (!Array.isArray(raw)) return [];
    const known = new Set(TOOLS.map((t) => t.id));
    return raw.filter((id): id is ToolId => typeof id === "string" && known.has(id as ToolId)).slice(0, FAVS_MAX);
  } catch {
    return [];
  }
}

export function writeFavs(ids: ToolId[]): void {
  try { localStorage.setItem(FAVS_KEY, JSON.stringify(ids.slice(0, FAVS_MAX))); } catch { /* private mode */ }
  window.dispatchEvent(new Event(EVENT));
}

/** Pin or unpin one tool; a seventh pin replaces the oldest. */
export function toggleFav(id: ToolId): ToolId[] {
  const now = readFavs();
  const next = now.includes(id) ? now.filter((x) => x !== id) : [...now, id].slice(-FAVS_MAX);
  writeFavs(next);
  return next;
}

/** The pinned tools, live across every component on the page. */
export function useFavs(): [ToolId[], (id: ToolId) => void] {
  const [favs, setFavs] = useState<ToolId[]>(readFavs);
  useEffect(() => {
    const sync = () => setFavs(readFavs());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener(EVENT, sync); window.removeEventListener("storage", sync); };
  }, []);
  const toggle = useCallback((id: ToolId) => { toggleFav(id); }, []);
  return [favs, toggle];
}
